/** Promise-shaped wrapper around the separation worker. */
import type { WorkerRequest, WorkerResponse } from './worker/separate.worker'

/**
 * Directory holding `manifest.json` and the model parts. Relative, so the whole
 * bundle can be served from a subpath -- and so pointing this at another origin
 * (a CDN or object store) is a one-line change if the split ever stops sufficing.
 */
export const MODEL_BASE_URL = 'models'

export interface ModelInfo {
  ep: string
  modelBytes: number
  downloadMs: number
  sessionMs: number
  /** Cost of the throwaway first inference that compiles the GPU shaders. */
  warmupMs: number
  fromCache: boolean
}

export interface DownloadState {
  loaded: number
  total: number
  cached: boolean
}

export interface AudioSegment {
  start: number
  inst0: Float32Array
  inst1: Float32Array
  voc0: Float32Array
  voc1: Float32Array
}

export interface SeparationResult {
  inst0: Float32Array
  inst1: Float32Array
  voc0: Float32Array
  voc1: Float32Array
  ms: number
}

export class SeparationEngine {
  private worker: Worker
  private onDownload?: (d: DownloadState) => void
  private onProgress?: (chunk: number, total: number) => void
  private onAudio?: (seg: AudioSegment) => void
  private pending: { resolve: (v: never) => void; reject: (e: Error) => void; kind: 'init' | 'run' } | null = null

  constructor() {
    this.worker = new Worker(new URL('./worker/separate.worker.ts', import.meta.url), { type: 'module' })
    this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => this.handle(e.data)
    this.worker.onerror = (e) => this.settleError(new Error(e.message || 'worker crashed'))
  }

  private handle(msg: WorkerResponse): void {
    switch (msg.type) {
      case 'download':
        this.onDownload?.({ loaded: msg.loaded, total: msg.total, cached: msg.cached })
        break
      case 'progress':
        this.onProgress?.(msg.chunk, msg.totalChunks)
        break
      case 'audio':
        this.onAudio?.({
          start: msg.start,
          inst0: msg.inst0,
          inst1: msg.inst1,
          voc0: msg.voc0,
          voc1: msg.voc1,
        })
        break
      case 'ready':
        this.settle('init', {
          ep: msg.ep,
          modelBytes: msg.modelBytes,
          downloadMs: msg.downloadMs,
          sessionMs: msg.sessionMs,
          warmupMs: msg.warmupMs,
          fromCache: msg.fromCache,
        })
        break
      case 'result':
        this.settle('run', {
          inst0: msg.inst0,
          inst1: msg.inst1,
          voc0: msg.voc0,
          voc1: msg.voc1,
          ms: msg.ms,
        })
        break
      case 'error':
        this.settleError(new Error(msg.message))
        break
    }
  }

  private settle(kind: 'init' | 'run', value: unknown): void {
    const p = this.pending
    if (!p || p.kind !== kind) return
    this.pending = null
    p.resolve(value as never)
  }

  private settleError(err: Error): void {
    const p = this.pending
    this.pending = null
    p?.reject(err)
  }

  private send<T>(msg: WorkerRequest, kind: 'init' | 'run', transfer: Transferable[] = []): Promise<T> {
    if (this.pending) return Promise.reject(new Error('engine is already busy'))
    return new Promise<T>((resolve, reject) => {
      this.pending = { resolve: resolve as (v: never) => void, reject, kind }
      this.worker.postMessage(msg, transfer)
    })
  }

  load(onDownload?: (d: DownloadState) => void): Promise<ModelInfo> {
    this.onDownload = onDownload
    // Resolve here, on the main thread: a relative URL inside the worker would
    // resolve against the worker script's own path, not the page.
    const modelBaseUrl = new URL(MODEL_BASE_URL, document.baseURI).href
    return this.send<ModelInfo>({ type: 'init', modelBaseUrl, preferWebGpu: true }, 'init')
  }

  run(
    ch0: Float32Array,
    ch1: Float32Array,
    sampleRate: number,
    onProgress?: (chunk: number, total: number) => void,
    onAudio?: (seg: AudioSegment) => void,
  ): Promise<SeparationResult> {
    this.onProgress = onProgress
    this.onAudio = onAudio
    // Copy before transferring: the caller keeps the originals for playback.
    const a = new Float32Array(ch0)
    const b = new Float32Array(ch1)
    return this.send({ type: 'separate', ch0: a, ch1: b, sampleRate }, 'run', [a.buffer, b.buffer])
  }

  dispose(): void {
    this.worker.terminate()
  }
}
